/**
 * The Forge's pages answer at the store's own word for it (`forgePathSegment` in
 * `@loot-os/api-contract`): `/workshop/mine` for a store that calls it the workshop. The route
 * tree keeps `/forge/…`, so every `to="/forge/…"` in code, the route ids analytics and Sentry
 * record, and the stored notification links stay one value across stores; the router rewrites
 * between the two, the store's word in the address bar and in every href, `/forge` inside.
 * `/forge/…` keeps answering under every name, for those notification links and old bookmarks,
 * and is replaced by the store's word once it lands.
 */
import { DEFAULT_FORGE_PATH_SEGMENT, forgePathSegment } from "@loot-os/api-contract";
import type { AnyRouter, LocationRewrite, ParsedLocation } from "@tanstack/react-router";
import { useEffect, useMemo, useRef } from "react";

import { useTerminology } from "./terminology.js";

/** An in-store href split at its first path segment: `/forge/mine?x` → `forge` + `/mine?x`. */
function splitFirstSegment(href: string): { head: string; rest: string } | null {
  if (!href.startsWith("/") || href.startsWith("//")) return null;
  const match = /^\/([^/?#]*)(.*)$/s.exec(href);
  return match ? { head: match[1] ?? "", rest: match[2] ?? "" } : null;
}

/** An address-bar href to the route tree's: `/workshop/mine` → `/forge/mine`. */
export function toRoutePath(href: string, segment: string): string {
  const parts = splitFirstSegment(href);
  if (!parts || parts.head.toLowerCase() !== segment) return href;
  return `/${DEFAULT_FORGE_PATH_SEGMENT}${parts.rest}`;
}

/** A route-tree href to the address bar's: `/forge/mine` → `/workshop/mine`. */
export function toPublicPath(href: string, segment: string): string {
  const parts = splitFirstSegment(href);
  if (!parts || parts.head.toLowerCase() !== DEFAULT_FORGE_PATH_SEGMENT) return href;
  return `/${segment}${parts.rest}`;
}

/**
 * The address bar's href with the Forge's word put right, or null when it already is (or is not
 * a forge page): the `/forge` alias under a store that renamed it, the word in other letter case
 * (`/Workshop`), or `formerSegment`, the word this page used until the config renamed it. Only the
 * segment is rewritten, so the rest of the href stays as encoded.
 */
export function canonicalForgeHref(
  publicHref: string,
  segment: string,
  formerSegment: string | null,
): string | null {
  const parts = splitFirstSegment(publicHref);
  if (!parts || parts.head === segment) return null;
  const head = parts.head.toLowerCase();
  if (head !== segment && head !== DEFAULT_FORGE_PATH_SEGMENT && head !== formerSegment) {
    return null;
  }
  return `/${segment}${parts.rest}`;
}

/** The router rewrite for a store's segment; none under the default, which needs no rewriting. */
export function forgeRewrite(segment: string): LocationRewrite | undefined {
  if (segment === DEFAULT_FORGE_PATH_SEGMENT) return undefined;
  return {
    input: ({ url }) => {
      url.pathname = toRoutePath(url.pathname, segment);
      return url;
    },
    output: ({ url }) => {
      url.pathname = toPublicPath(url.pathname, segment);
      return url;
    },
  };
}

/** The store's first path segment for the Forge's pages. */
export function useForgePathSegment(): string {
  const terms = useTerminology();
  return forgePathSegment(terms);
}

/**
 * The Forge's routing for `router` under `segment`: the rewrite to hand `RouterProvider` (always,
 * `undefined` included: RouterProvider updates the router only with the options it is given, so
 * leaving it out would keep a previous word's rewrite), and the address bar put right
 * (`canonicalForgeHref`), in place with its history state, whenever a navigation resolves on the
 * wrong word. The router corrects the address it first loads by itself; this covers the rest: a
 * back or forward onto an entry saved as `/forge/…`, and a rename that arrives after the first load
 * (the page painted from a cached config, then the fresh one names the Forge differently), which
 * moves the address from the old word to the new one.
 */
export function useForgeRouting(router: AnyRouter, segment: string): LocationRewrite | undefined {
  const rewrite = useMemo(() => forgeRewrite(segment), [segment]);

  useEffect(() => {
    const correct = (location: ParsedLocation) => {
      const href = canonicalForgeHref(location.publicHref, segment, null);
      if (href) router.history.replace(href, router.history.location.state);
    };
    return router.subscribe("onResolved", ({ toLocation }) => correct(toLocation));
  }, [router, segment]);

  // A cached config that named the Forge differently put the page under its word; once the fresh
  // one renames it, move the address onto the new word (a replace, which loads it again), or match
  // the address again when it was not a forge page.
  const matchedUnder = useRef(segment);
  useEffect(() => {
    const former = matchedUnder.current;
    if (former === segment) return;
    matchedUnder.current = segment;
    const href = canonicalForgeHref(router.history.location.href, segment, former);
    if (href) router.history.replace(href, router.history.location.state);
    else void router.invalidate();
  }, [router, segment]);

  return rewrite;
}
